import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, Users, MessageCircle, ArrowLeft, Search, PenSquare, MoreHorizontal, SmilePlus, Paperclip, Upload, Loader2, Phone, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { chatApi, CardData, ChatMessage, Conversation, TeamMember, AttachmentData, uploadChatAttachment, sendSMSReply } from '../../utils/chatApi';
import { UserAvatar } from '../common/UserAvatar';
import { CardMessage } from './CardMessage';
import { FileCard } from './FileCard';
import { ImageMessage } from './ImageMessage';
import LinkPreview, { renderTextWithLinks, extractUrls } from './LinkPreview';

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNewMessage?: (message: ChatMessage) => void;
  onUnreadCountChange?: (count: number) => void;
}

export interface ChatPanelHandle {
  addIncomingMessage: (message: ChatMessage) => void;
  updateMessage: (message: ChatMessage) => void;
  removeMessage: (messageId: string) => void;
  startDraftWithCard: (cardData: CardData) => void;
  startSMSConversation: (petOwnerId: string, petOwnerName: string, phone: string) => void;
}

type View = 'conversations' | 'chat' | 'new-message';

/**
 * Facebook-style chat panel that slides out from the right side.
 * Supports 1:1 DMs and practice-wide broadcast messages.
 */
const ChatPanel = forwardRef<ChatPanelHandle, ChatPanelProps>(({ isOpen, onClose, onNewMessage, onUnreadCountChange }, ref) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('conversations');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<TeamMember | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [composerCard, setComposerCard] = useState<CardData | null>(null);
  const [pendingDraftCard, setPendingDraftCard] = useState<CardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmConversation, setDeleteConfirmConversation] = useState<Conversation | null>(null);
  const [menuOpenConversationId, setMenuOpenConversationId] = useState<string | null>(null);
  // Message-level state for edit/delete
  const [menuOpenMessageId, setMenuOpenMessageId] = useState<string | null>(null);
  const [reactionPickerOpenMessageId, setReactionPickerOpenMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState<ChatMessage | null>(null);
  // File attachment state
  const [pendingAttachment, setPendingAttachment] = useState<{ file: File; previewUrl?: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Expose methods to handle incoming WebSocket messages
  useImperativeHandle(ref, () => ({
    addIncomingMessage: (message: ChatMessage) => {
      // Only add if we're in the relevant conversation
      const currentConversationId = selectedConversation?.conversation_id;
      if (message.conversation_id === currentConversationId) {
        // Avoid duplicates - just append the new message
        setMessages(prev => {
          if (prev.some(m => m.message_id === message.message_id)) {
            return prev;
          }
          return [...prev, message];
        });
      } else {
        // Not in this conversation - just update conversations list
        loadConversations();
      }
    },
    updateMessage: (message: ChatMessage) => {
      // Update a message (edits, reactions, etc.)
      setMessages(prev => prev.map(m =>
        m.message_id === message.message_id
          ? { ...m, ...message }
          : m
      ));
    },
    removeMessage: (messageId: string) => {
      // Remove a deleted message
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
    },
    startDraftWithCard: (cardData: CardData) => {
      setPendingDraftCard(cardData);
      setComposerCard(null);
      setNewMessage('');
      setSelectedConversation(null);
      setSelectedRecipient(null);
      setView('new-message');
      loadTeamMembers();
    },
    startSMSConversation: (petOwnerId: string, petOwnerName: string, phone: string) => {
      // Create a synthetic SMS conversation and open it
      const smsConversation: Conversation = {
        conversation_id: `sms_${petOwnerId}`,
        conversation_type: 'sms_external',
        participant_ids: [],
        participant_names: [petOwnerName],
        participant_photo_urls: [],
        last_message: null,
        last_message_at: null,
        unread_count: 0,
        is_external: true,
        external_name: petOwnerName,
        external_phone: phone,
        external_pet_owner_id: petOwnerId,
      };
      setSelectedConversation(smsConversation);
      setSelectedRecipient(null);
      setComposerCard(null);
      setNewMessage('');
      setView('chat');
      // Load any existing messages for this conversation
      loadMessages(smsConversation.conversation_id, undefined);
    },
  }));

  // Load conversations on mount
  useEffect(() => {
    if (isOpen && user) {
      loadConversations();
      loadTeamMembers();
    }
  }, [isOpen, user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when entering chat view
  useEffect(() => {
    if (view === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [view]);

  // Close menu when view changes or clicking outside
  useEffect(() => {
    setMenuOpenConversationId(null);
  }, [view]);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpenConversationId(null);
    if (menuOpenConversationId) {
      // Delay to avoid immediate close on the same click
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [menuOpenConversationId]);

  // Close message menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setMenuOpenMessageId(null);
    if (menuOpenMessageId) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [menuOpenMessageId]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setReactionPickerOpenMessageId(null);
    if (reactionPickerOpenMessageId) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [reactionPickerOpenMessageId]);

  const COMMON_EMOJIS = ['👍', '❤️', '😂', '🎉', '😮', '😢', '🙏'];

  const applyLocalReaction = (messageId: string, emoji: string, add: boolean) => {
    const userId = user?.id;
    if (!userId) return;

    setMessages(prev => prev.map(m => {
      if (m.message_id !== messageId) return m;
      const reactions = { ...(m.reactions || {}) };
      const users = new Set<string>(reactions[emoji] || []);
      if (add) users.add(userId);
      else users.delete(userId);
      if (users.size === 0) delete reactions[emoji];
      else reactions[emoji] = Array.from(users);
      return { ...m, reactions };
    }));
  };

  const handleToggleReaction = async (message: ChatMessage, emoji: string) => {
    const userId = user?.id;
    if (!userId) return;
    const existing = message.reactions?.[emoji] || [];
    const hasReacted = existing.includes(userId);
    const action = hasReacted ? 'remove' : 'add';

    // Optimistic UI update
    applyLocalReaction(message.message_id, emoji, action === 'add');

    try {
      await chatApi.reactToMessage(message.message_id, { emoji, action });
    } catch (err) {
      // Revert optimistic change on failure
      applyLocalReaction(message.message_id, emoji, action !== 'add');
      console.error('Failed to react to message:', err);
    }
  };

  const renderReactionsRow = (message: ChatMessage, isMine: boolean) => {
    const reactions = message.reactions || {};
    const entries = Object.entries(reactions)
      .map(([emoji, users]) => ({ emoji, count: users?.length || 0, users: users || [] }))
      .filter(r => r.count > 0)
      .sort((a, b) => b.count - a.count);

    if (entries.length === 0) return null;

    const userId = user?.id;
    return (
      <div className={`mt-1 flex flex-wrap gap-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
        {entries.map(({ emoji, count, users }) => {
          const mine = !!userId && users.includes(userId);
          return (
            <button
              key={emoji}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleReaction(message, emoji);
              }}
              className={`
                px-2 py-0.5 rounded-full text-xs border transition-colors
                ${mine
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white dark:bg-dark-bg-secondary text-gray-800 dark:text-dark-text-primary border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                }
              `}
              title={users.join(', ')}
            >
              <span>{emoji}</span>
              <span className="ml-1">{count}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await chatApi.getConversations();
      setConversations(data);
      // Calculate total unread count from INTERNAL conversations only (external SMS don't trigger notifications)
      const totalUnread = data
        .filter(conv => !conv.is_external)
        .reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      onUnreadCountChange?.(totalUnread);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const data = await chatApi.getTeamMembers();
      setTeamMembers(data);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const loadMessages = async (conversationId?: string, recipientId?: string) => {
    try {
      setLoading(true);
      const data = await chatApi.getMessages({ conversationId, recipientId, limit: 50 });
      setMessages(data.reverse()); // Reverse to show oldest first
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildConversationIdForRecipient = (recipientId: string): string | null => {
    if (!user?.practice_id || !user?.id) return null;
    const practiceId = user.practice_id;
    if (recipientId === 'ALL') {
      return `PRACTICE#${practiceId}#BROADCAST`;
    }
    const sorted = [user.id, recipientId].sort();
    return `PRACTICE#${practiceId}#DM#${sorted[0]}#${sorted[1]}`;
  };

  const handleSelectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setView('chat');
    setComposerCard(null);
    setPendingDraftCard(null);
    loadMessages(conversation.conversation_id);
    
    // Clear unread count for this conversation locally and update total
    if (conversation.unread_count > 0) {
      setConversations(prev => {
        const updated = prev.map(c => 
          c.conversation_id === conversation.conversation_id 
            ? { ...c, unread_count: 0 }
            : c
        );
        // Recalculate total unread (internal only)
        const totalUnread = updated
          .filter(conv => !conv.is_external)
          .reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        onUnreadCountChange?.(totalUnread);
        return updated;
      });
      
      // Persist read status to backend (fire and forget)
      chatApi.markConversationRead(conversation.conversation_id).catch(err => {
        console.error('Failed to mark conversation as read:', err);
      });
    }
  };

  const handleSelectRecipient = (member: TeamMember) => {
    setSelectedRecipient(member);
    setView('chat');

    const convId = buildConversationIdForRecipient(member.id);
    // Set a synthetic selectedConversation so websocket + optimistic messages stay in sync
    setSelectedConversation({
      conversation_id: convId || '',
      conversation_type: member.id === 'ALL' ? 'broadcast' : 'dm',
      participant_ids: member.id === 'ALL' ? [] : [member.id],
      participant_names: member.id === 'ALL' ? ['Team Broadcast'] : [member.full_name],
      participant_photo_urls: member.id === 'ALL' ? [] : (member.photo_url ? [member.photo_url] : []),
      last_message: null,
      last_message_at: null,
      unread_count: 0,
    });

    if (pendingDraftCard) {
      setComposerCard(pendingDraftCard);
      setPendingDraftCard(null);
      setNewMessage('');
    } else {
      setComposerCard(null);
    }

    if (convId) {
      loadMessages(convId, undefined);
    } else {
      loadMessages(undefined, member.id);
    }
  };

  // Handle SMS reply to pet owner
  const handleSendSMSReply = async () => {
    const hasText = newMessage.trim().length > 0;
    const hasAttachment = pendingAttachment !== null;
    
    if (!hasText && !hasAttachment) return;
    if (!selectedConversation?.external_pet_owner_id) return;

    try {
      const messageText = newMessage.trim();
      const attachmentToSend = pendingAttachment;
      
      setNewMessage(''); // Clear input immediately
      setPendingAttachment(null); // Clear attachment
      
      let mediaAttachments: { url: string; content_type: string; s3_key?: string; filename?: string }[] | undefined;
      
      // If we have an attachment, upload it to S3 first
      if (attachmentToSend) {
        setIsUploading(true);
        try {
          const attachmentData = await uploadChatAttachment(attachmentToSend.file);
          
          // Build media attachment for MMS
          mediaAttachments = [{
            url: attachmentData.url,
            content_type: attachmentData.content_type,
            s3_key: attachmentData.s3_key,
            filename: attachmentData.filename,
          }];
          
          // Clean up preview URL
          if (attachmentToSend.previewUrl) {
            URL.revokeObjectURL(attachmentToSend.previewUrl);
          }
        } catch (uploadError) {
          console.error('Failed to upload MMS attachment:', uploadError);
          alert('Failed to upload file. Please try again.');
          setNewMessage(messageText);
          setPendingAttachment(attachmentToSend);
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      const response = await sendSMSReply(selectedConversation.external_pet_owner_id, {
        message_text: messageText,
        media_attachments: mediaAttachments,
      });

      if (response.success) {
        // Optimistic update - add message to local state
        const optimisticMsg: ChatMessage = {
          message_id: response.message_sid || `temp-${Date.now()}`,
          conversation_id: selectedConversation.conversation_id,
          sender_id: user?.id || '',
          sender_name: user?.full_name || 'You',
          recipient_id: selectedConversation.external_pet_owner_id,
          message_text: messageText,
          created_at: Date.now(),
          message_type: response.is_mms ? 'mms' : 'sms',
          read_by: [user?.id || ''],
          is_external: true,
          direction: 'outbound',
          media_attachments: mediaAttachments?.map(att => ({
            url: att.url,
            content_type: att.content_type,
            s3_key: att.s3_key,
            filename: att.filename,
            is_image: att.content_type.startsWith('image/'),
            is_video: att.content_type.startsWith('video/'),
          })),
        };

        setMessages((prev) => [...prev, optimisticMsg]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      } else {
        // Show error
        alert(`Failed to send ${mediaAttachments ? 'MMS' : 'SMS'}: ${response.error || 'Unknown error'}`);
        setNewMessage(messageText); // Restore message
      }
    } catch (error) {
      console.error('Failed to send SMS/MMS:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !composerCard && !pendingAttachment) return;
    
    // Handle SMS/MMS reply for external conversations
    if (isExternalConversation() && selectedConversation?.external_pet_owner_id) {
      // handleSendSMSReply now handles both text and attachments (as MMS)
      await handleSendSMSReply();
      return;
    }
    
    const recipientId =
      selectedRecipient?.id ||
      (selectedConversation?.conversation_type === 'broadcast'
        ? 'ALL'
        : selectedConversation?.participant_ids?.[0]);

    if (!recipientId) return;

    try {
      const noteText = newMessage.trim();
      const cardToSend = composerCard;
      const attachmentToSend = pendingAttachment;

      // If user wrote a note AND attached a card, send TWO messages:
      // 1) the card, then 2) the note (so conversation preview is the note, and thread shows both).
      const optimistic: ChatMessage[] = [];

      // Handle file attachment upload first
      if (attachmentToSend) {
        setIsUploading(true);
        try {
          // Upload file to S3 and get attachment data
          const attachmentData = await uploadChatAttachment(attachmentToSend.file);
          
          const attachmentResponse = await chatApi.sendMessage({
            recipient_id: recipientId,
            message_text: `Shared: ${attachmentToSend.file.name}`,
            message_type: attachmentData.type, // 'image' or 'file'
            attachment_data: attachmentData,
          });

          optimistic.push({
            message_id: attachmentResponse.message_id,
            conversation_id: selectedConversation?.conversation_id || buildConversationIdForRecipient(recipientId) || '',
            sender_id: user?.id || '',
            sender_name: user?.full_name || 'You',
            recipient_id: recipientId,
            message_text: `Shared: ${attachmentToSend.file.name}`,
            created_at: attachmentResponse.timestamp,
            message_type: attachmentData.type,
            read_by: [user?.id || ''],
            attachment_data: attachmentData,
          });

          // Clean up preview URL
          if (attachmentToSend.previewUrl) {
            URL.revokeObjectURL(attachmentToSend.previewUrl);
          }
        } catch (uploadError) {
          console.error('Failed to upload attachment:', uploadError);
          alert('Failed to upload file. Please try again.');
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      if (cardToSend) {
        const cardResponse = await chatApi.sendMessage({
          recipient_id: recipientId,
          message_text: `Shared: ${cardToSend.title}`,
          message_type: 'card',
          card_data: cardToSend,
        });

        optimistic.push({
          message_id: cardResponse.message_id,
          conversation_id: selectedConversation?.conversation_id || buildConversationIdForRecipient(recipientId) || '',
          sender_id: user?.id || '',
          sender_name: user?.full_name || 'You',
          recipient_id: recipientId,
          message_text: `Shared: ${cardToSend.title}`,
          created_at: cardResponse.timestamp,
          message_type: 'card',
          read_by: [user?.id || ''],
          card_data: cardToSend,
        });
      }

      if (noteText) {
        const textResponse = await chatApi.sendMessage({
          recipient_id: recipientId,
          message_text: noteText,
          message_type: 'text',
        });

        optimistic.push({
          message_id: textResponse.message_id,
          conversation_id: selectedConversation?.conversation_id || buildConversationIdForRecipient(recipientId) || '',
          sender_id: user?.id || '',
          sender_name: user?.full_name || 'You',
          recipient_id: recipientId,
          message_text: noteText,
          created_at: textResponse.timestamp,
          message_type: 'text',
          read_by: [user?.id || ''],
        });
      }

      setMessages(prev => [...prev, ...optimistic]);
      setNewMessage('');
      setComposerCard(null);
      setPendingAttachment(null);

      // Notify parent of new message(s)
      optimistic.forEach((m) => onNewMessage?.(m));
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !newMessage && composerCard) {
      e.preventDefault();
      setComposerCard(null);
    }
    if (e.key === 'Backspace' && !newMessage && pendingAttachment) {
      e.preventDefault();
      if (pendingAttachment.previewUrl) {
        URL.revokeObjectURL(pendingAttachment.previewUrl);
      }
      setPendingAttachment(null);
    }
  };

  // File drag-drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (view === 'chat') {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the container (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (view !== 'chat') return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    // Max file size: 25MB
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 25MB');
      return;
    }

    // Create preview for images
    let previewUrl: string | undefined;
    if (file.type.startsWith('image/')) {
      previewUrl = URL.createObjectURL(file);
    }

    // Clean up previous preview if any
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }

    setPendingAttachment({ file, previewUrl });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = () => {
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }
    setPendingAttachment(null);
  };

  const handleBack = () => {
    if (view === 'chat') {
      setSelectedConversation(null);
      setSelectedRecipient(null);
      setMessages([]);
      setView('conversations');
    } else if (view === 'new-message') {
      setView('conversations');
    }
  };

  const handleDeleteConversation = async (conversation: Conversation) => {
    try {
      await chatApi.deleteConversation(conversation.conversation_id);
      // Remove from local state
      setConversations(prev => prev.filter(c => c.conversation_id !== conversation.conversation_id));
      setDeleteConfirmConversation(null);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const handleDeleteMessage = async (message: ChatMessage) => {
    try {
      await chatApi.deleteMessage(message.message_id);
      // Remove from local state
      setMessages(prev => prev.filter(m => m.message_id !== message.message_id));
      setDeleteConfirmMessage(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const handleStartEdit = (message: ChatMessage) => {
    setEditingMessageId(message.message_id);
    setEditingText(message.message_text);
    setMenuOpenMessageId(null);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editingText.trim()) return;
    
    try {
      const updatedMessage = await chatApi.editMessage(editingMessageId, editingText.trim());
      // Update in local state
      setMessages(prev => prev.map(m => 
        m.message_id === editingMessageId 
          ? { ...m, message_text: updatedMessage.message_text, edited_at: updatedMessage.edited_at }
          : m
      ));
      setEditingMessageId(null);
      setEditingText('');
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  };

  const filteredTeamMembers = teamMembers.filter(member =>
    member.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getConversationTitle = () => {
    if (selectedRecipient) {
      return selectedRecipient.full_name;
    }
    if (selectedConversation?.conversation_type === 'broadcast') {
      return 'Team Broadcast';
    }
    if (selectedConversation?.is_external) {
      return selectedConversation?.external_name || 'Unknown';
    }
    return selectedConversation?.participant_names?.[0] || 'Chat';
  };
  
  const isExternalConversation = () => {
    return selectedConversation?.is_external === true;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-y-0 right-0 w-80 md:w-96 bg-white dark:bg-dark-bg-secondary shadow-xl z-50 flex flex-col border-l border-gray-200 dark:border-dark-border"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        {view !== 'conversations' ? (
          <button
            onClick={handleBack}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <MessageCircle className="w-5 h-5" />
        )}
        
        <h2 className="font-semibold text-lg flex-1 ml-3 truncate flex items-center gap-2">
          {view === 'conversations' && 'Team Chat'}
          {view === 'new-message' && 'New Message'}
          {view === 'chat' && (
            <>
              {isExternalConversation() && selectedConversation?.external_pet_owner_id ? (
                <button
                  onClick={() => {
                    navigate(`/dashboard/pet_owners/${selectedConversation.external_pet_owner_id}`);
                  }}
                  className="hover:underline cursor-pointer text-left"
                  title="View pet owner profile"
                >
                  {getConversationTitle()}
                </button>
              ) : (
                getConversationTitle()
              )}
              {isExternalConversation() && (
                <span className="px-2 py-0.5 text-xs rounded bg-amber-500/30 text-amber-200 font-medium">
                  SMS
                </span>
              )}
            </>
          )}
        </h2>
        
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-full transition-colors ml-1"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Conversations List */}
        {view === 'conversations' && (
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <MessageCircle className="w-12 h-12 text-gray-300 dark:text-dark-text-muted mb-3" />
                <p className="text-gray-500 dark:text-dark-text-secondary">No conversations yet</p>
                <p className="text-sm text-gray-400 dark:text-dark-text-muted mt-1">
                  Tap the compose button to start
                </p>
              </div>
            ) : (
              <>
                {/* Internal conversations (Team Messages) */}
                {conversations.filter(c => !c.is_external).length > 0 && (
                  <>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wide bg-gray-50 dark:bg-dark-bg-tertiary">
                      Team Messages
                    </div>
                    {conversations.filter(c => !c.is_external).map((conv) => (
                      <div
                        key={conv.conversation_id}
                        className="group relative w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
                      >
                        <button
                          onClick={() => handleSelectConversation(conv)}
                          className="flex-1 flex items-start gap-3 text-left min-w-0"
                        >
                          {conv.conversation_type === 'broadcast' ? (
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                          ) : (
                            <UserAvatar
                              photoUrl={conv.participant_photo_urls?.[0]}
                              fullName={conv.participant_names[0] || 'Unknown'}
                              size="md"
                              className="flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900 dark:text-dark-text-primary truncate">
                                {conv.conversation_type === 'broadcast' ? 'Team Broadcast' : conv.participant_names[0]}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-dark-text-secondary truncate">
                              {conv.last_message || 'No messages'}
                            </p>
                          </div>
                        </button>
                        <div className="flex-shrink-0 w-[72px] flex flex-col items-end gap-1">
                          {conv.last_message_at ? (
                            <button
                              onClick={() => handleSelectConversation(conv)}
                              className="text-xs text-gray-500 dark:text-dark-text-muted hover:underline"
                              title="Open"
                            >
                              {formatTime(conv.last_message_at)}
                            </button>
                          ) : (
                            <div className="h-[16px]" />
                          )}
                          <div className="flex items-center justify-end gap-2 w-full">
                            {conv.unread_count > 0 ? (
                              <button
                                onClick={() => handleSelectConversation(conv)}
                                className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full"
                                title="Open unread"
                              >
                                {conv.unread_count}
                              </button>
                            ) : (
                              <div className="w-[24px]" />
                            )}
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenConversationId(menuOpenConversationId === conv.conversation_id ? null : conv.conversation_id);
                                }}
                                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary transition-colors"
                                title="More"
                              >
                                <MoreHorizontal className="w-5 h-5 text-gray-500 dark:text-dark-text-muted" />
                              </button>
                              {menuOpenConversationId === conv.conversation_id && (
                                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg py-1 z-10 min-w-[140px]">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setMenuOpenConversationId(null); setDeleteConfirmConversation(conv); }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
                                  >
                                    Delete Chat
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {/* External SMS conversations (Pet Owners) */}
                {conversations.filter(c => c.is_external).length > 0 && (
                  <>
                    <div className="border-t border-gray-200 dark:border-dark-border" />
                    <div className="px-4 py-2 text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2">
                      <Phone className="w-3 h-3" />
                      SMS - Pet Owners
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </div>
                    {conversations.filter(c => c.is_external).map((conv) => (
                      <div
                        key={conv.conversation_id}
                        className="group relative w-full px-4 py-3 flex items-start gap-3 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-colors"
                      >
                        <button
                          onClick={() => handleSelectConversation(conv)}
                          className="flex-1 flex items-start gap-3 text-left min-w-0"
                        >
                          <div className="relative flex-shrink-0">
                            {conv.external_photo_url ? (
                              <img 
                                src={conv.external_photo_url} 
                                alt={conv.external_name || 'Pet owner'} 
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                  {(conv.external_name || 'U').charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
                              <Phone className="w-2.5 h-2.5 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 dark:text-dark-text-primary truncate">
                                {conv.external_name || 'Unknown'}
                              </span>
                              <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 font-medium flex-shrink-0">
                                SMS
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-dark-text-secondary truncate">
                              {conv.last_message || 'No messages'}
                            </p>
                          </div>
                        </button>
                        <div className="flex-shrink-0 w-[72px] flex flex-col items-end gap-1">
                          {conv.last_message_at && (
                            <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                              {formatTime(conv.last_message_at)}
                            </span>
                          )}
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenConversationId(menuOpenConversationId === conv.conversation_id ? null : conv.conversation_id);
                              }}
                              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary transition-colors"
                              title="More"
                            >
                              <MoreHorizontal className="w-5 h-5 text-gray-500 dark:text-dark-text-muted" />
                            </button>
                            {menuOpenConversationId === conv.conversation_id && (
                              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg py-1 z-10 min-w-[140px]">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setMenuOpenConversationId(null); setDeleteConfirmConversation(conv); }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
                                >
                                  Delete Chat
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Floating New Message Button - Instagram style */}
        {view === 'conversations' && (
          <button
            onClick={() => setView('new-message')}
            className="absolute bottom-6 right-6 w-14 h-14 bg-gray-800 dark:bg-gray-700 hover:bg-gray-700 dark:hover:bg-gray-600 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95"
            title="New Message"
          >
            <PenSquare className="w-6 h-6 text-white" />
          </button>
        )}

        {/* New Message - Select Recipient */}
        {view === 'new-message' && (
          <div>
            <div className="p-3 border-b border-gray-200 dark:border-dark-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-100 dark:bg-dark-bg-tertiary border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:text-dark-text-primary"
                />
              </div>
            </div>
            
            {/* Broadcast option */}
            <button
              onClick={() => handleSelectRecipient({ id: 'ALL', full_name: 'Team Broadcast', role: '', is_online: false })}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors border-b border-gray-100 dark:border-dark-border"
            >
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-900 dark:text-dark-text-primary">
                  Team Broadcast
                </div>
                <div className="text-sm text-gray-500 dark:text-dark-text-secondary">
                  Send to everyone
                </div>
              </div>
            </button>

            <div className="divide-y divide-gray-100 dark:divide-dark-border">
              {filteredTeamMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleSelectRecipient(member)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  <UserAvatar
                    photoUrl={member.photo_url}
                    fullName={member.full_name}
                    size="md"
                    showOnlineIndicator={true}
                    isOnline={member.is_online}
                  />
                  <div className="text-left flex-1">
                    <div className="font-medium text-gray-900 dark:text-dark-text-primary">
                      {member.full_name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-dark-text-secondary">
                      {member.role}
                    </div>
                  </div>
                  {member.is_online && (
                    <span className="text-xs text-green-600 dark:text-green-400">Online</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat Messages */}
        {view === 'chat' && (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <MessageCircle className="w-10 h-10 text-gray-300 dark:text-dark-text-muted mb-2" />
                  <p className="text-gray-500 dark:text-dark-text-secondary">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              ) : (
                messages.map((message) => {
                  // For SMS messages, use direction to determine alignment
                  // outbound (practice sent) = right side, inbound (pet owner sent) = left side
                  const isMine = message.is_external 
                    ? message.direction === 'outbound'
                    : message.sender_id === user?.id;
                  const isEditing = editingMessageId === message.message_id;
                  
                  return (
                    <div
                      key={message.message_id}
                      className={`group flex items-start gap-1 ${isMine ? 'justify-end' : 'justify-start'}`}
                    >
                      {/* Reaction button (left of bubble for own messages) */}
                      {isMine && !isEditing && (
                        <div className="relative flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setReactionPickerOpenMessageId(
                                reactionPickerOpenMessageId === message.message_id ? null : message.message_id
                              );
                              setMenuOpenMessageId(null);
                            }}
                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary"
                            title="React"
                          >
                            <SmilePlus className="w-4 h-4 text-gray-400" />
                          </button>

                          {reactionPickerOpenMessageId === message.message_id && (
                            <div className="absolute left-0 top-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg p-2 z-50">
                              <div className="flex gap-1">
                                {COMMON_EMOJIS.map((emoji) => (
                                  <button
                                    key={emoji}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleReaction(message, emoji);
                                      setReactionPickerOpenMessageId(null);
                                    }}
                                    className="w-8 h-8 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
                                    title={emoji}
                                  >
                                    <span className="text-lg leading-none">{emoji}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Menu button for own messages - left side */}
                      {isMine && !isEditing && (
                        <div className="relative flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenMessageId(
                                menuOpenMessageId === message.message_id ? null : message.message_id
                              );
                              setReactionPickerOpenMessageId(null);
                            }}
                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary"
                          >
                            <MoreHorizontal className="w-4 h-4 text-gray-400" />
                          </button>
                          
                          {/* Dropdown menu */}
                          {menuOpenMessageId === message.message_id && (
                            <div className="absolute left-0 top-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg py-1 z-50 min-w-[100px]">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEdit(message);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenMessageId(null);
                                  setDeleteConfirmMessage(message);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Card message - renders its own container */}
                      {message.message_type === 'card' && message.card_data ? (
                        <div className="max-w-[80%]">
                          {!isMine && (
                            <p className="text-xs font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                              {message.sender_name}
                            </p>
                          )}
                          <CardMessage cardData={message.card_data} isMine={isMine} />
                          {renderReactionsRow(message, isMine)}
                          <p className={`text-xs mt-1 ${isMine ? 'text-gray-400' : 'text-gray-400 dark:text-dark-text-muted'}`}>
                            {formatTime(message.created_at)}
                          </p>
                        </div>
                      ) : message.message_type === 'image' && message.attachment_data ? (
                        /* Image message */
                        <div className="max-w-[80%]">
                          {!isMine && (
                            <p className="text-xs font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                              {message.sender_name}
                            </p>
                          )}
                          <ImageMessage attachment={message.attachment_data} isMine={isMine} />
                          {renderReactionsRow(message, isMine)}
                          <p className={`text-xs mt-1 ${isMine ? 'text-gray-400' : 'text-gray-400 dark:text-dark-text-muted'}`}>
                            {formatTime(message.created_at)}
                          </p>
                        </div>
                      ) : message.message_type === 'file' && message.attachment_data ? (
                        /* File message */
                        <div className="max-w-[80%]">
                          {!isMine && (
                            <p className="text-xs font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                              {message.sender_name}
                            </p>
                          )}
                          <FileCard attachment={message.attachment_data} isMine={isMine} />
                          {renderReactionsRow(message, isMine)}
                          <p className={`text-xs mt-1 ${isMine ? 'text-gray-400' : 'text-gray-400 dark:text-dark-text-muted'}`}>
                            {formatTime(message.created_at)}
                          </p>
                        </div>
                      ) : (
                        /* Regular text message bubble */
                        <div
                          className={`
                            max-w-[80%] px-3 py-2 rounded-2xl
                            ${isMine
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary rounded-bl-sm'
                            }
                          `}
                        >
                          {!isMine && (
                            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                              {message.sender_name}
                            </p>
                          )}
                          
                          {isEditing ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit();
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                                className="w-full px-2 py-1 text-sm text-gray-900 bg-white rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                autoFocus
                              />
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-xs text-blue-200 hover:text-white"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveEdit}
                                  className="text-xs text-white font-medium hover:underline"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {/* Message text */}
                              {message.message_text && (
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {renderTextWithLinks(message.message_text || '', isMine)}
                                </p>
                              )}
                              
                              {/* MMS Media Attachments */}
                              {message.media_attachments && message.media_attachments.length > 0 && (
                                <div className={`mt-2 flex flex-col gap-2 ${!message.message_text ? '' : ''}`}>
                                  {message.media_attachments.map((media, idx) => (
                                    <div key={idx} className="relative">
                                      {media.is_image || media.content_type?.startsWith('image/') ? (
                                        <a 
                                          href={media.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="block"
                                        >
                                          <img
                                            src={media.url}
                                            alt={media.filename || 'MMS image'}
                                            className="max-w-[200px] max-h-[200px] rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                          />
                                        </a>
                                      ) : media.is_video || media.content_type?.startsWith('video/') ? (
                                        <video
                                          src={media.url}
                                          controls
                                          className="max-w-[250px] max-h-[200px] rounded-lg"
                                          preload="metadata"
                                        >
                                          Your browser does not support video playback.
                                        </video>
                                      ) : (
                                        <a
                                          href={media.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                                            isMine 
                                              ? 'bg-blue-400/30 hover:bg-blue-400/40' 
                                              : 'bg-gray-200 dark:bg-dark-bg-tertiary hover:bg-gray-300 dark:hover:bg-dark-bg-hover'
                                          } transition-colors`}
                                        >
                                          <Paperclip className="w-4 h-4" />
                                          <span className="text-sm truncate max-w-[150px]">
                                            {media.filename || 'Download attachment'}
                                          </span>
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Link previews */}
                              {extractUrls(message.message_text || '').slice(0, 1).map((url) => (
                                <LinkPreview key={url} url={url} isMine={isMine} />
                              ))}
                            </>
                          )}
                          
                          {renderReactionsRow(message, isMine)}

                          <p className={`text-xs mt-1 ${isMine ? 'text-blue-200' : 'text-gray-400 dark:text-dark-text-muted'}`}>
                            {formatTime(message.created_at)}
                            {message.edited_at && (
                              <span className="ml-1">(edited)</span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Reaction button (right of bubble for other people's messages) */}
                      {!isMine && !isEditing && (
                        <div className="relative flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setReactionPickerOpenMessageId(
                                reactionPickerOpenMessageId === message.message_id ? null : message.message_id
                              );
                              setMenuOpenMessageId(null);
                            }}
                            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary"
                            title="React"
                          >
                            <SmilePlus className="w-4 h-4 text-gray-400" />
                          </button>

                          {reactionPickerOpenMessageId === message.message_id && (
                            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg p-2 z-50">
                              <div className="flex gap-1">
                                {COMMON_EMOJIS.map((emoji) => (
                                  <button
                                    key={emoji}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleReaction(message, emoji);
                                      setReactionPickerOpenMessageId(null);
                                    }}
                                    className="w-8 h-8 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
                                    title={emoji}
                                  >
                                    <span className="text-lg leading-none">{emoji}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Message Input */}
      {view === 'chat' && (
        <div className="p-3 border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
          {/* Card preview */}
          {composerCard && (
            <div className="mb-2 flex justify-end">
              <div className="max-w-[80%]">
                <CardMessage cardData={composerCard} isMine={true} />
                <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-muted text-right">
                  Press <span className="font-semibold">Backspace</span> on an empty message to remove the card.
                </p>
              </div>
            </div>
          )}
          
          {/* Attachment preview */}
          {pendingAttachment && (
            <div className="mb-2 flex justify-end">
              <div className="relative max-w-[80%]">
                {pendingAttachment.previewUrl ? (
                  // Image preview
                  <div className="relative rounded-xl overflow-hidden">
                    <img 
                      src={pendingAttachment.previewUrl} 
                      alt={pendingAttachment.file.name}
                      className="max-w-full max-h-[150px] object-cover"
                    />
                    <button
                      onClick={handleRemoveAttachment}
                      className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  // File preview card
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-dark-bg-tertiary rounded-xl">
                    <Paperclip className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-dark-text-primary truncate max-w-[200px]">
                      {pendingAttachment.file.name}
                    </span>
                    <button
                      onClick={handleRemoveAttachment}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-muted text-right">
                  Press <span className="font-semibold">Backspace</span> on an empty message to remove.
                </p>
              </div>
            </div>
          )}
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar"
          />
          
          <div className="flex items-center gap-2">
            {/* Attachment button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors text-gray-500 dark:text-dark-text-muted"
              title="Attach file"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onKeyDown={handleInputKeyDown}
              disabled={isUploading}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-dark-bg-tertiary border-0 rounded-full text-sm focus:ring-2 focus:ring-blue-500 dark:text-dark-text-primary placeholder-gray-400"
            />
            <button
              onClick={handleSendMessage}
              disabled={isUploading || (!newMessage.trim() && !composerCard && !pendingAttachment)}
              className={`
                p-2 rounded-full transition-colors
                ${isUploading
                  ? 'bg-blue-400 text-white cursor-wait'
                  : (newMessage.trim() || composerCard || pendingAttachment)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 dark:bg-dark-bg-tertiary text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Delete Conversation Confirmation Dialog */}
      {deleteConfirmConversation && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl p-6 mx-4 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">
              Delete Conversation?
            </h3>
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-4">
              This will remove the conversation from your view. The other person will still be able to see it.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmConversation(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteConversation(deleteConfirmConversation)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Message Confirmation Dialog */}
      {deleteConfirmMessage && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl p-6 mx-4 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">
              Delete Message?
            </h3>
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-4">
              This will permanently delete the message for everyone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmMessage(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteMessage(deleteConfirmMessage)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drag-and-drop overlay */}
      {isDragging && view === 'chat' && (
        <div className="absolute inset-0 bg-blue-600/90 flex items-center justify-center z-50 pointer-events-none">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
              <Upload className="w-8 h-8 text-white" />
            </div>
            <p className="text-white text-lg font-medium">Drop file to upload</p>
            <p className="text-white/70 text-sm mt-1">Images, PDFs, and documents supported</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default ChatPanel;

